import { UseMutateFunction } from "react-query";
import { TFunction } from "i18next";
import { ProviderContext } from "notistack";
import { DateTime } from "luxon";
import { RemoteApiError } from "../../../hooks/Remote/useRemote/types";
import { CheckinDto, CheckinResponseDto } from "../typings";
import { BookingType } from "../../Booking-Form/typings/booking-inputs";
import { CheckInSettings } from "../../Login/typings/login.types";
import { calMinuteInterval } from "../../../components/BookingForm/functions/date-time.functions";
import { getLuxonByTime } from "../../../utils/utilities";

export type MutateCheckin = UseMutateFunction<
  CheckinResponseDto,
  RemoteApiError,
  CheckinDto,
  unknown
>;

const CHECKIN_API_ERROR_TRANSLATION_KEYS = {
  "Booking is already checked in!": "_checkin_errMsgAlreadyCheckedIn",
  "User is not in radius to check in!": "_checkin_errMsgNotWithinRange",
  "Check-In denied due to collision with previous booking!": "_checkin_errMsgBookingCollision",
  "Check-In not possible buffer time elapsed!": "_checkin_errMsgBufferElapsed"
} as const;

export function getCheckinErrorTranslationKey(message: string | undefined): string | null {
  if (!message) {
    return null;
  }
  return (
    CHECKIN_API_ERROR_TRANSLATION_KEYS[
      message as keyof typeof CHECKIN_API_ERROR_TRANSLATION_KEYS
    ] ?? null
  );
}

/** Attempts to checkin into a booked workplace or conference zone */
export function attemptCheckin(
  scheduleId: number,
  bookingType: BookingType,
  checkinSettings: CheckInSettings[],
  mutateCheckin: MutateCheckin,
  { t, enqueueSnackbar }: { t: TFunction; enqueueSnackbar: ProviderContext["enqueueSnackbar"] }
) {
  const geoRequired = checkinSettings.find(
    setting => setting.bookingType === bookingType
  )?.geoLocationRequired;

  // make sure if geoCheckIn is not enabled, then try checkin without navigator
  if (!geoRequired) {
    return mutateCheckin({
      latitude: 0,
      longitude: 0,
      bookingId: scheduleId,
      bookingType
    });
  }

  // from browser, get location and position
  navigator.geolocation.getCurrentPosition(
    position => {
      mutateCheckin({
        latitude: position.coords.latitude,
        longitude: position.coords.longitude,
        bookingId: scheduleId,
        bookingType
      });
    },
    () => {
      enqueueSnackbar(t("_checkin_enableGeolocationToCheckin"), {
        variant: "error"
      });
    }
  );
}

/** handle checkin response */
export function handleCheckinResponse(
  state: {
    checkinRes: CheckinResponseDto | undefined;
    checkinError: RemoteApiError | null;
    errAvailabilityTimeOnTheFly: RemoteApiError | null;
    isCheckinPerBookingType: boolean;
    geoLocationRequired: boolean;
    checkinOnTheFly: boolean;
    isSameTimeNow?: boolean;
  },
  action: {
    t: TFunction;
    enqueueSnackbar: ProviderContext["enqueueSnackbar"];
    refetchAllSchedule?: () => void;
    setShowDialog?: (s: boolean) => void;
  }
) {
  const {
    checkinRes,
    checkinError,
    errAvailabilityTimeOnTheFly,
    isCheckinPerBookingType,
    geoLocationRequired,
    checkinOnTheFly,
    isSameTimeNow
  } = state;
  const { t, enqueueSnackbar, refetchAllSchedule, setShowDialog } = action;

  // handle checkin Error
  handleCheckinError(checkinError, errAvailabilityTimeOnTheFly, t, enqueueSnackbar);

  // handle response
  if (checkinRes) {
    const { checkedIn, withinRange, metersRemaining } = checkinRes;
    const kmRemaining = (metersRemaining / 1000).toFixed(2);

    // handle not within range
    if (!withinRange && geoLocationRequired) {
      return enqueueSnackbar(t("_checkin_warnMsgMetersRemaining", { distance: kmRemaining }), {
        variant: "warning"
      });
    }

    // handle checkedIn
    handleCheckedIn(
      { checkedIn, isCheckinPerBookingType, checkinOnTheFly, isSameTimeNow },
      { setShowDialog, refetchAllSchedule, t, enqueueSnackbar }
    );
  }
}

function handleCheckinError(
  checkinError: RemoteApiError | null,
  errAvailabilityTimeOnTheFly: RemoteApiError | null,
  t: TFunction,
  enqueueSnackbar: ProviderContext["enqueueSnackbar"]
) {
  if (checkinError) {
    const translationKey = getCheckinErrorTranslationKey(checkinError.response?.data?.message);
    if (translationKey) {
      return enqueueSnackbar(t(translationKey), { variant: "error" });
    }
    return enqueueSnackbar(t("_checkin_errMsgUnknown"), { variant: "error" });
  }

  if (errAvailabilityTimeOnTheFly) {
    if (errAvailabilityTimeOnTheFly.message.includes("disabled")) {
      return enqueueSnackbar(t("_checkin_errAvailabilityDisabled"), { variant: "error" });
    } else return enqueueSnackbar(t("_checkin_errAvailabilityUnknown"), { variant: "error" });
  }
}

/**
 * handle success and return snackbar message
 * according to, if spontaneous booking via QR-code with check-in setting per booking type or only checked-in via QR-Code
 * window.location.reload(); is removed
 */
function handleCheckedIn(
  state: {
    checkedIn: boolean;
    isCheckinPerBookingType: boolean;
    checkinOnTheFly: boolean;
    isSameTimeNow?: boolean;
  },
  action: {
    setShowDialog: ((s: boolean) => void) | undefined;
    refetchAllSchedule: (() => void) | undefined;
    t: TFunction;
    enqueueSnackbar: ProviderContext["enqueueSnackbar"];
  }
) {
  const { checkedIn, isCheckinPerBookingType, checkinOnTheFly, isSameTimeNow } = state;
  const { setShowDialog, refetchAllSchedule, t, enqueueSnackbar } = action;
  if (checkedIn) {
    const checkinOnTheFlyMsg =
      isCheckinPerBookingType && isSameTimeNow
        ? t("_checkin_bookOnTheFly_success") // "Booked and checked-in successfully",
        : t("_onlyBook_success"); // "Booked successfully",
    const successMsg = checkinOnTheFly ? checkinOnTheFlyMsg : t("_checkin_success"); // "Successfully checked in",
    enqueueSnackbar(successMsg, { variant: "success" });
    setShowDialog?.(false);
    refetchAllSchedule?.();
  }
}

export const getLaterTime = (standardTime: string, useMinuteInterval: boolean = false): string => {
  const now = useMinuteInterval
    ? DateTime.now().set({ minute: calMinuteInterval(DateTime.now()) })
    : DateTime.now();
  const standard = getLuxonByTime(standardTime);
  return DateTime.fromMillis(Math.max(standard.toMillis(), now.toMillis())).toFormat("HH:mm");
};
